// features/findings/charts/parallel-coordinates.js
// Parallel coordinates — multi-axis comparison.
// One vertical axis per dimension; one polyline per entity.

export function ParallelCoordinates({ title, dimensions, entities }) {
  if (!dimensions || dimensions.length < 2 || !entities || entities.length < 1) return null;

  const W = 720;
  const H = 280;
  const ML = 60;
  const MR = 100;
  const MT = 32;
  const MB = 36;
  const innerW = W - ML - MR;
  const innerH = H - MT - MB;
  const axisCount = dimensions.length;
  const axisX = (i) => ML + (i / (axisCount - 1)) * innerW;

  // For each dimension, scale the entity values to [0, 1] then to inner Y
  const scaleY = (val, dim) => {
    const range = dim.max - dim.min;
    if (range === 0) return MT + innerH / 2;
    const norm = (val - dim.min) / range;
    return MT + (1 - norm) * innerH;
  };

  return (
    <figure className="finding-chart">
      <figcaption className="finding-chart-caption">{title}</figcaption>
      <div className="finding-svg-wrap">
        <svg viewBox={`0 0 ${W} ${H}`} className="finding-svg" role="img" aria-label={title}>
          {/* Axes */}
          {dimensions.map((dim, i) => (
            <g key={dim.id}>
              <line
                x1={axisX(i)} y1={MT}
                x2={axisX(i)} y2={H - MB}
                className="finding-svg-axis"
              />
              <text
                x={axisX(i)} y={H - 8}
                className="finding-svg-axis-title"
                textAnchor="middle"
              >
                {dim.label}
              </text>
              {/* min / max value labels */}
              <text x={axisX(i)} y={MT - 8} className="finding-svg-axis-label" textAnchor="middle">
                {dim.maxLabel ?? dim.max}
              </text>
              <text x={axisX(i)} y={H - MB + 14} className="finding-svg-axis-label" textAnchor="middle">
                {dim.minLabel ?? dim.min}
              </text>
            </g>
          ))}

          {/* Entity polylines */}
          {entities.map((e, ei) => {
            const points = dimensions.map((dim, i) => {
              const v = e.values[dim.id];
              const x = axisX(i);
              const y = v != null ? scaleY(v, dim) : MT + innerH / 2;
              return { x, y };
            });
            const path = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
            const lastP = points[points.length - 1];
            return (
              <g key={e.id}>
                <path d={path} className={`finding-svg-line finding-svg-line-${e.color} finding-pcoord-line`} />
                {points.map((p, i) => (
                  <circle key={i} cx={p.x} cy={p.y} r={3.5}
                    className={`finding-svg-dot finding-svg-dot-${e.color}`} />
                ))}
                <text
                  x={lastP.x + 10}
                  y={lastP.y + (ei * 14 - (entities.length - 1) * 7)}
                  className={`finding-svg-series-label finding-svg-series-${e.color}`}
                  dominantBaseline="middle"
                >
                  {e.label}
                </text>
              </g>
            );
          })}
        </svg>
      </div>
    </figure>
  );
}

export default ParallelCoordinates;
